import React from 'react';
import { Listbox } from '@headlessui/react';
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline';

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectDropdownProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder: string;
  label?: string;
  error?: string;
  theme?: 'pink' | 'orange' | 'teal';
  variant?: 'default' | 'form' | 'signup';
  disabled?: boolean;
  required?: boolean;
  otherOptionValue?: string;
  otherValue?: string;
  onOtherChange?: (value: string) => void;
  onOtherBlur?: () => void;
  otherError?: string;
  otherPlaceholder?: string;
  otherRequired?: boolean;
  otherInputName?: string;
}

const themeClasses = {
  pink: {
    button: 'focus:ring-pink-500 focus:border-pink-500',
    optionActive: 'bg-pink-50 text-pink-900',
    check: 'text-pink-600',
  },
  orange: {
    button: 'focus:ring-orange-500 focus:border-orange-500',
    optionActive: 'bg-orange-50 text-orange-900',
    check: 'text-orange-600',
  },
  teal: {
    button: 'focus:ring-teal-500 focus:border-teal-500',
    optionActive: 'bg-teal-50 text-teal-900',
    check: 'text-teal-600',
  },
};

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder,
  label,
  error,
  theme = 'pink',
  variant = 'default',
  disabled = false,
  required = false,
  otherOptionValue,
  otherValue = '',
  onOtherChange,
  onOtherBlur,
  otherError,
  otherPlaceholder = 'Please specify...',
  otherRequired = false,
  otherInputName = 'multi-select-other',
}) => {
  const selected = value || [];
  const themeStyle = themeClasses[theme];
  const isFormVariant = variant === 'form';
  const isSignupVariant = variant === 'signup';
  const showOtherInput = !!otherOptionValue && selected.includes(otherOptionValue);
  const hasError = !!(error || otherError);

  const getSingleSelectionLabel = () => {
    const selectedValue = selected[0];
    if (selectedValue === otherOptionValue && otherValue.trim()) {
      return otherValue.trim();
    }
    return options.find(o => o.value === selectedValue)?.label ?? selectedValue;
  };

  const displayText =
    selected.length === 0
      ? placeholder
      : selected.length === 1
        ? getSingleSelectionLabel()
        : `${selected.length} selected`;

  const buttonClassName = isSignupVariant
    ? `signup-form-input relative cursor-pointer pr-8 text-left${hasError ? ' signup-form-input--invalid' : ''}`
    : isFormVariant
      ? `form-input relative cursor-pointer pr-8 text-left ${hasError ? 'invalid-input' : ''}`
      : `relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm focus:outline-none focus:ring-1 sm:text-sm ${themeStyle.button} ${hasError ? 'border-red-500' : ''}`;

  const optionsClassName = isSignupVariant
    ? 'absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-xl border border-gray-300 bg-white py-1 text-sm shadow-lg focus:outline-none'
    : isFormVariant
      ? 'absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-[20px] border border-gray-400 bg-white py-1 text-xs shadow-md focus:outline-none'
      : 'absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm';

  const optionClassName = (active: boolean) =>
    isSignupVariant
      ? `relative cursor-default select-none py-2 pl-8 pr-3 text-sm ${active ? 'bg-pink-50 text-blue-900' : 'text-blue-900'}`
      : isFormVariant
        ? `relative cursor-default select-none py-2 pl-8 pr-3 text-xs ${active ? 'bg-gray-100' : 'text-gray-900'}`
        : `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? themeStyle.optionActive : 'text-gray-900'}`;

  const errorClassName = isSignupVariant
    ? 'signup-field-error'
    : isFormVariant
      ? 'apply-input-error'
      : 'mt-1 text-sm text-red-600';

  return (
    <div className="relative">
      {label && (
        <>
          <div className="row">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
            {required && <span className="text-red-500 ml-1">*</span>}
          </div>
        </>
      )}
      <Listbox value={selected} onChange={onChange} multiple disabled={disabled}>
        <Listbox.Button className={buttonClassName}>
          <span
            className={`block truncate ${selected.length === 0 ? (isSignupVariant ? 'signup-form-input--placeholder' : isFormVariant ? '' : 'text-gray-500') : isFormVariant || isSignupVariant ? '' : 'text-gray-900'}`}
          >
            {displayText}
          </span>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronDownIcon
              className={isFormVariant || isSignupVariant ? 'h-4 w-4 text-gray-500' : 'h-5 w-5 text-gray-400'}
              aria-hidden="true"
            />
          </span>
        </Listbox.Button>
        <Listbox.Options className={optionsClassName}>
          {options.map(option => (
            <Listbox.Option
              key={option.value}
              value={option.value}
              className={({ active }) => optionClassName(active)}
            >
              {({ selected: isSelected }) => (
                <>
                  <span className={`block truncate ${isSelected ? 'font-medium' : 'font-normal'}`}>
                    {option.label}
                  </span>
                  {isSelected ? (
                    <span
                      className={`absolute inset-y-0 left-0 flex items-center ${isFormVariant || isSignupVariant ? 'pl-2 text-pink-600' : `pl-3 ${themeStyle.check}`}`}
                    >
                      <CheckIcon
                        className={isFormVariant || isSignupVariant ? 'h-4 w-4' : 'h-5 w-5'}
                        aria-hidden="true"
                      />
                    </span>
                  ) : null}
                </>
              )}
            </Listbox.Option>
          ))}
          {showOtherInput && (
            <div className="border-t border-gray-200 px-2 py-2" onClick={event => event.stopPropagation()}>
              <input
                className={
                  isSignupVariant
                    ? `signup-form-input${otherError ? ' signup-form-input--invalid' : ''}`
                    : `form-input ${otherError ? 'invalid-input' : ''}`
                }
                type="text"
                name={otherInputName}
                id={otherInputName}
                value={otherValue}
                placeholder={otherPlaceholder}
                required={otherRequired}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                data-lpignore="true"
                data-1p-ignore="true"
                aria-label={otherPlaceholder}
                onChange={event => onOtherChange?.(event.target.value)}
                onBlur={onOtherBlur}
                onMouseDown={event => event.stopPropagation()}
                onClick={event => event.stopPropagation()}
                onKeyDown={event => event.stopPropagation()}
              />
              {otherError && <p className={errorClassName}>{otherError}</p>}
            </div>
          )}
        </Listbox.Options>
      </Listbox>
      {error && <p className={errorClassName}>{error}</p>}
      {!error && otherError && <p className={errorClassName}>{otherError}</p>}
    </div>
  );
};

export default MultiSelectDropdown;
